'use client';

import { forwardRef, useImperativeHandle, useState, useCallback, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useShallow } from 'zustand/react/shallow';
import { UnifiedImageUpload, type UnifiedImageUploadRef } from '@/shared/ui';
import { UnifiedUploadModal } from './UnifiedUploadModal';
import { useUploadStore } from '@/features/upload';
import { useCheckLimit, useSubscriptionLimits } from '@/app/model/stores';
import {
  createAngleForConcept,
  replaceAngle,
} from '@/features/angle/lib/angle-operations';
import { cacheInvalidation } from '@/shared/lib/cache-invalidation';
import type { AngleStoreResponse } from '@/types/concept';
import type { ProcessedFile } from '@/shared/lib/services/image-upload.service';
import type { UploadAngle } from '@/types/upload';
import type { ConceptAngleTypeType } from '@/shared/constants';
import type {
  MediaSourceExtraOption,
  MediaSourceProjectAngle,
} from '@/shared/ui/MediaSourceSelector';
import { ClassTypes } from '@/shared/constants/class-types';
import {
  findClosestAspectRatio,
  ASPECT_RATIOS,
} from '@/shared/constants/aspect-ratios';
import { createLogger } from '@/shared/lib/logger';
import { anglesService } from '@/shared/api/services/angles.service';
import { useLimitErrorPaywall } from '@/shared/hooks';
import { toast } from '@/shared/lib/toast';
import {
  setMutationLock,
  clearMutationLock,
} from '@/shared/api/websocket/realtime-sync.locks';

const logger = createLogger('AngleUpload');

interface AngleUploadProps {
  conceptId?: string;
  /**
   * Upload target when no conceptId is set: angles are created in the
   * project library (POST /projects/{id}/angles) and attached to a concept
   * later (ConceptAnglesEditModal save / new-concept creation). Every
   * consumer must provide either conceptId or projectId — the server
   * requires width/height/aspect_ratio plus an owner context to issue an
   * upload policy.
   */
  projectId?: string;
  onSuccess: (angles: AngleStoreResponse | AngleStoreResponse[]) => void;
  onError?: (error: string) => void;
  onCancel?: () => void;
  className?: string;
  children?: React.ReactNode;
  onLimitReached?: () => void;
  currentAngleCount?: number;
  /**
   * Pre-flight check for the per-project angle library limit
   * (plan.limit_project_angles). When set and the count is >= the limit,
   * uploading/replacing is blocked with onProjectAngleLibraryLimitReached.
   * Replacement still counts against this limit because the server stores
   * each upload as a new angle record in the project library.
   */
  currentProjectAngleLibraryCount?: number;
  onProjectAngleLibraryLimitReached?: () => void;
  allowMultiple?: boolean;
  showValidation?: boolean;
  angleType?: ConceptAngleTypeType;
  uploadTitle?: string;
  uploadDescription?: string;
  mode?: 'create' | 'replace';
  replaceConceptAngleId?: string;
  /** Additional options for MediaSourceSelector (e.g. "Select from library") */
  mediaSourceExtraOptions?: MediaSourceExtraOption[];
  /** Optional gallery of project angles shown in the MediaSourceSelector. */
  mediaSourceProjectAngles?: MediaSourceProjectAngle[];
  /** Callback when an existing project angle thumbnail is tapped. */
  onMediaSourceProjectAngleSelected?: (angleId: string) => void;
  /** Auto-open file dialog on mount */
  autoOpen?: boolean;
}

export interface AngleUploadRef {
  openFileDialog: () => void;
}

export const AngleUpload = forwardRef<AngleUploadRef, AngleUploadProps>(function AngleUpload({
  conceptId,
  projectId,
  onSuccess,
  onError,
  onCancel,
  className = '',
  children,
  onLimitReached,
  currentAngleCount = 0,
  currentProjectAngleLibraryCount,
  onProjectAngleLibraryLimitReached,
  allowMultiple = false,
  showValidation = true,
  angleType,
  uploadTitle,
  uploadDescription,
  mode = 'create',
  replaceConceptAngleId,
  mediaSourceExtraOptions,
  mediaSourceProjectAngles,
  onMediaSourceProjectAngleSelected,
  autoOpen = false,
}, forwardedRef) {
  const uploadRef = useRef<UnifiedImageUploadRef>(null);
  const [uploadedAngles, setUploadedAngles] = useState<UploadAngle[]>([]);
  const [uploadPhase, setUploadPhase] = useState<
    'idle' | 'uploading' | 'validating' | 'complete'
  >('idle');
  const [showModal, setShowModal] = useState(false);
  const [uploadProgressData, setUploadProgressData] = useState<
    { percentage: number; currentFile: number; totalFiles: number } | undefined
  >();

  const checkLimit = useCheckLimit();
  const limits = useSubscriptionLimits();
  const t = useTranslations('angles');

  // Server-driven paywall routing for typed 422 limit codes. The project-
  // library path preserves the existing preflight-drift warning log; the
  // concept-angle path reuses the in-concept upgrade surface already wired
  // via `onLimitReached` (both the main-angle modal and the derivative flow
  // open `showUpgradePrompt` here).
  const resetUploadUiOnLimit = useCallback(() => {
    setUploadPhase('idle');
    setShowModal(false);
  }, []);

  const routeLimitErrorToPaywall = useLimitErrorPaywall({
    onAngleLibraryLimit: () => {
      if (currentProjectAngleLibraryCount !== undefined) {
        logger.warn(
          'AngleUpload preflight missed: server rejected but cache present'
        );
      }
      resetUploadUiOnLimit();
      onProjectAngleLibraryLimitReached?.();
    },
    onConceptAngleLimit: () => {
      resetUploadUiOnLimit();
      onLimitReached?.();
    },
  });

  // Upload store state and actions
  const { createBatch, uploadBatch, uploadSingle } = useUploadStore();

  // Real upload progress aggregated from the store (XHR byte progress flows
  // into item.progress via upload-recovery → image-upload.service). Undefined
  // while no upload is in flight.
  const storeProgress = useUploadStore(
    useShallow(
      (
        state
      ):
        | { percentage: number; currentFile: number; totalFiles: number }
        | undefined => {
        if (!state.ui.loading.upload) return undefined;
        const batchId = state.ui.currentBatchId;
        const batch = batchId ? state.batches[batchId] : null;
        if (batch && batch.items.length > 0) {
          const completed = batch.items.filter(
            (item) => item.status === 'completed'
          ).length;
          const percentage = Math.round(
            batch.items.reduce((sum, item) => sum + item.progress, 0) /
              batch.items.length
          );
          return {
            percentage,
            currentFile: Math.min(completed + 1, batch.items.length),
            totalFiles: batch.items.length,
          };
        }
        const item = state.queue[0];
        return { percentage: item?.progress ?? 0, currentFile: 1, totalFiles: 1 };
      }
    )
  );

  // Mirror store progress into local state so the last real value is retained
  // during the uploading→validating transition (the store flips loading off
  // before the phase state updates — clearing here would flash an empty bar).
  useEffect(() => {
    if (storeProgress) {
      setUploadProgressData(storeProgress);
    }
  }, [storeProgress]);

  // API callback for creating or replacing angles
  const createAnglesApi = useCallback(
    async (processedFiles: ProcessedFile[]) => {
      const uploadData = [];

      for (let i = 0; i < processedFiles.length; i++) {
        const file = processedFiles[i];
        if (!file) continue;

        // aspect_ratio is required by the server and must be snapped to the
        // supported whitelist (Gemini output orientation pinning).
        const aspectRatioKey = findClosestAspectRatio(file.width, file.height);
        const aspectRatio = ASPECT_RATIOS[aspectRatioKey].geminiValue;

        if (mode === 'replace' && replaceConceptAngleId && conceptId) {
          const angleResponse = await replaceAngle(
            conceptId,
            replaceConceptAngleId,
            file.file.name,
            file.md5,
            file.file.type,
            file.width,
            file.height,
            aspectRatio
          );
          uploadData.push({
            id: angleResponse.id,
            uploadPolicy: angleResponse.attributes.upload_policy,
          });
        } else if (conceptId) {
          const angleResponse = await createAngleForConcept(
            conceptId,
            file.file.name,
            file.md5,
            file.file.type,
            angleType,
            file.width,
            file.height,
            aspectRatio
          );
          uploadData.push({
            id: angleResponse.id,
            uploadPolicy: angleResponse.attributes.upload_policy,
          });
        } else if (projectId) {
          // No concept yet — upload into the project library. The angle is
          // attached to a concept later (ConceptAnglesEditModal save /
          // new-concept creation), matching the Apple/Android picker flows.
          const response = await anglesService.createProjectAngle(
            projectId,
            file.file.name,
            file.md5,
            file.file.type,
            file.width,
            file.height,
            aspectRatio
          );
          const uploadPolicy = response.attributes.upload_policy;
          if (!uploadPolicy) {
            // upload_policy is null only for soft-deleted angles, which
            // cannot happen on the create path — fail loudly instead of
            // silently skipping the upload.
            throw new Error('Project angle created without upload policy');
          }
          uploadData.push({ id: response.id, uploadPolicy });
        } else {
          // Programming error — see the projectId prop doc.
          throw new Error('AngleUpload requires a conceptId or projectId');
        }
      }

      return uploadData;
    },
    [conceptId, projectId, angleType, mode, replaceConceptAngleId]
  );

  const handleUpload = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      // Lock angles entity to prevent WebSocket-triggered concept refetch
      // before the uploaded file is available on CDN (prevents negative caching)
      setMutationLock('angles', 30_000);
      try {
        // Show modal and set uploading phase. Seed honest initial progress —
        // real byte progress streams in via the store selector above.
        setShowModal(true);
        setUploadPhase('uploading');
        setUploadProgressData({
          percentage: 0,
          currentFile: 1,
          totalFiles: files.length,
        });

        // Confirm runs after S3 success for every target — the server
        // HeadObject-checks R2, marks the angle confirmed and only then
        // starts validation / emits the `angles/created` realtime event.
        const confirm = async (id: string) => {
          await anglesService.confirmAngle(id);
        };

        let results;

        if (files.length === 1) {
          // Single file upload. The guard is unreachable (length === 1) but
          // must still clean up the modal — the finally only releases the lock.
          const file = files[0];
          if (!file) {
            setShowModal(false);
            setUploadPhase('idle');
            return;
          }

          const result = await uploadSingle(
            file,
            'angle',
            async (processedFile) => {
              const angles = await createAnglesApi([processedFile]);
              const uploadData = angles[0];
              if (!uploadData) {
                throw new Error('Failed to create angle');
              }
              return uploadData;
            },
            confirm
          );
          results = [result];
        } else {
          // Batch upload — `mainIndex` undefined: callers without conceptId go
          // through the concept-creation modal which owns the main-angle pick;
          // conceptId-bound callers don't need a main pick (single-angle path).
          const batchId = createBatch(files, 'angle');
          results = await uploadBatch(batchId, createAnglesApi, confirm);
        }

        // Library uploads must surface in the Views list immediately as
        // pending-validation rows — the WS validation event arrives much later.
        if (!conceptId && projectId) {
          cacheInvalidation.afterCreateProjectAngle(projectId);
        }

        // Create UploadAngle objects for validation. `is_main` is server-owned
        // here — concept-creation flows pick the main angle in
        // ConceptAnglesEditModal after upload.
        const angles: UploadAngle[] = results.map((result, index) => ({
          type: ClassTypes.ANGLE,
          id: result.id,
          attributes: {
            original_filename: files[index]?.name || 'Image',
            validation_performed_at: null,
            resized_file_name: '',
            resized_file_url: null,
            resized_file_width: 0,
            resized_file_height: 0,
            resized_file_size: 0,
            uploaded_at: new Date().toISOString(),
            is_main: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        }));

        setUploadedAngles(angles);

        if (showValidation) {
          // Transition to validation phase
          setUploadPhase('validating');
        } else {
          // Complete without validation
          setUploadPhase('complete');
          setShowModal(false);
          // Map results to AngleStoreResponse format
          const angleResponses = results.map(
            (result) =>
              ({
                id: result.id,
                attributes: {},
              }) as AngleStoreResponse
          );
          const response =
            angleResponses.length === 1 ? angleResponses[0] : angleResponses;
          if (response) {
            onSuccess(response);
          }
        }

      } catch (error) {
        // Server routes both project-library and concept-angle paywalls via
        // typed 422 codes — delegate to the hook (same surfaces the
        // preflights use) and skip the generic error dialog for those cases.
        if (routeLimitErrorToPaywall(error)) {
          return;
        }
        logger.error('Upload failed:', error);
        // Fatal upload failures (422 duplicate md5 on replace, etc.) surface
        // as a toast + flow dismiss, not an inline error modal — the progress
        // dialog is meaningless once the flow is dead. Mirrors Android
        // MultiImageUploadViewModel.reportFatalFailure and Apple
        // MultiImageUploadManager.reportFatalFailure.
        const message =
          error instanceof Error ? error.message : t('uploadFailed');
        toast.error(message);
        setShowModal(false);
        setUploadedAngles([]);
        setUploadPhase('idle');
        if (onError) {
          onError(message);
        }
        onCancel?.();
      } finally {
        // Covers every exit (success, error, early return) — leaking the lock
        // would freeze WS-driven angle invalidation for its full 30s TTL.
        clearMutationLock('angles');
      }
    },
    [
      conceptId,
      projectId,
      createAnglesApi,
      uploadSingle,
      createBatch,
      uploadBatch,
      showValidation,
      onSuccess,
      onError,
      onCancel,
      routeLimitErrorToPaywall,
      t,
    ]
  );

  // Pre-flight gate: returning false vetoes the whole flow inside
  // UnifiedImageUpload (no crop queue, no upload), so a paywall never opens
  // underneath an active crop modal.
  const handleFilesSelected = useCallback(
    (files: File[]): boolean => {
      // Replace flow: server now permits replace even when the library is full
      // (it rewrites the existing slot), so no preflight — just let the server
      // decide. The catch-block maps ANGLE_LIBRARY_LIMIT_EXCEEDED to paywall.
      if (mode === 'replace') {
        return true;
      }

      // Add-flow project-library pre-flight. We need both the current count
      // AND the plan limit; if either is missing skip the preflight and rely
      // on the 422 ANGLE_LIBRARY_LIMIT_EXCEEDED surface instead of guessing
      // (a 0 default would spuriously open the paywall on a fresh page load
      // before subscription/state hydrates).
      if (onProjectAngleLibraryLimitReached) {
        const projectAngleLimit = limits?.project_angles;
        if (
          currentProjectAngleLibraryCount === undefined ||
          projectAngleLimit === undefined
        ) {
          logger.warn('AngleUpload preflight bypassed: missing cache');
        } else if (
          typeof projectAngleLimit === 'number' &&
          projectAngleLimit > 0 &&
          currentProjectAngleLibraryCount + files.length > projectAngleLimit
        ) {
          onProjectAngleLibraryLimitReached();
          return false;
        }
      }

      // Check concept-angle limit before allowing upload.
      const totalAngles = currentAngleCount + files.length;
      if (!checkLimit('angles', totalAngles - 1)) {
        if (onLimitReached) {
          onLimitReached();
        }
        return false;
      }

      return true;
    },
    [
      checkLimit,
      limits,
      currentAngleCount,
      currentProjectAngleLibraryCount,
      onLimitReached,
      onProjectAngleLibraryLimitReached,
      mode,
    ]
  );

  const handleFilesProcessed = useCallback(
    async (files: File[]) => {
      logger.info('Files processed (cropped):', files.length);
      // Multi-file flows without a conceptId hand the picked images straight
      // to ConceptAnglesEditModal (created post-upload) — that modal owns the
      // main-angle pick and reorder UX, so no pre-upload selector is needed
      // here. Keeps WEB in parity with Apple/Android.
      await handleUpload(files);
    },
    [handleUpload]
  );

  const handleUploadError = useCallback(
    (error: Error) => {
      if (onError) {
        onError(error.message);
      }
    },
    [onError]
  );

  const handleValidationComplete = useCallback(
    (validAngles: UploadAngle[]) => {
      // Mark as complete and close modal
      setUploadPhase('complete');
      setShowModal(false);

      // Map back to AngleStoreResponse format
      const angleResponses = validAngles.map(
        (angle) =>
          ({
            id: angle.id,
            attributes: {},
          }) as AngleStoreResponse
      );

      const response =
        angleResponses.length === 1 ? angleResponses[0] : angleResponses;
      if (response) {
        onSuccess(response);
      }
      setUploadedAngles([]);
      setUploadPhase('idle');
    },
    [onSuccess]
  );

  const handleValidationRetry = useCallback(() => {
    // Don't close the modal - just reset state and open file dialog
    setUploadedAngles([]);
    setUploadPhase('idle');
    // Open file dialog immediately
    uploadRef.current?.openFileDialog();
  }, []);

  const handleModalClose = useCallback(() => {
    setShowModal(false);
    setUploadedAngles([]);
    setUploadPhase('idle');
  }, []);

  // Auto-open MediaSourceSelector on mount
  useEffect(() => {
    if (autoOpen) {
      // Small delay to ensure refs are attached
      const timer = setTimeout(() => {
        uploadRef.current?.openFileDialog();
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [autoOpen]);

  useImperativeHandle(
    forwardedRef,
    () => ({ openFileDialog: () => uploadRef.current?.openFileDialog() }),
    []
  );

  return (
    <>
      <UnifiedImageUpload
        ref={uploadRef}
        uploadType="angle"
        mode={mode}
        conceptId={conceptId}
        allowMultiple={allowMultiple}
        maxFiles={allowMultiple ? 10 : 1}
        allowCrop={true}
        showAspectRatioSelector={true}
        showRendersOption={true}
        mediaSourceExtraOptions={mediaSourceExtraOptions}
        mediaSourceProjectAngles={mediaSourceProjectAngles}
        onMediaSourceProjectAngleSelected={onMediaSourceProjectAngleSelected}
        className={className}
        onFilesSelected={handleFilesSelected}
        onFilesProcessed={handleFilesProcessed}
        onUploadError={handleUploadError}
        uploadTitle={uploadTitle}
        uploadDescription={uploadDescription}
      >
        {children}
      </UnifiedImageUpload>

      <UnifiedUploadModal
        isOpen={showModal}
        phase={uploadPhase}
        uploadProgress={uploadProgressData}
        angles={uploadedAngles}
        onClose={handleModalClose}
        onCancel={onCancel}
        onRetry={handleValidationRetry}
        onComplete={handleValidationComplete}
      />
    </>
  );
});
